import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LayoutDashboard, User as UserIcon, LogOut, LogIn, UserPlus, ChevronDown } from "lucide-react";
import { useAuth } from "../lib/auth";
import AuthModal from "./AuthModal";
import { resolveAsset } from "../lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/tournaments", label: "Tournaments" },
  { to: "/teams", label: "Teams" },
  { to: "/leaderboard", label: "Leaderboard" },
  { to: "/hall-of-fame", label: "Hall of Fame" },
  { to: "/play-of-the-week", label: "Play of the Week" },
  { to: "/rules", label: "Rules" },
  { to: "/support", label: "Support" },
];

function initials(s) {
  if (!s) return "?";
  return s.split(/\s|_/).filter(Boolean).map((x) => x[0]).slice(0, 2).join("").toUpperCase();
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const h = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    navigate("/");
  };

  return (
    <>
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5 flex-shrink-0 group">
            <img
              src="/brand/logo.png"
              alt="Spicy Community"
              data-testid="brand-logo"
              className="h-9 w-9 rounded-full object-cover ring-1 ring-red-500/40 shadow-[0_0_18px_rgba(230,0,0,0.55)] group-hover:shadow-[0_0_28px_rgba(230,0,0,0.9)] transition-shadow"
            />
            <span className="font-display font-extrabold text-lg tracking-tight">
              SPICY<span className="text-red-500">.</span>COMMUNITY
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-6">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive ? "text-white" : "text-zinc-400 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen((v) => { const next = !v; if (next) setOpen(false); return next; })}
                  data-testid="nav-user-menu"
                  className="flex items-center gap-2 glass-dark rounded-full pl-1.5 pr-3 py-1.5 hover:border-red-500/40 transition-all"
                >
                  <UserAvatar user={user} size={28} />
                  <span className="text-sm font-semibold hidden sm:inline-block">{user.username || user.name}</span>
                  <ChevronDown size={12} className="text-zinc-500" />
                </button>
                {menuOpen && (
                  <div
                    data-testid="user-dropdown"
                    className="absolute right-0 mt-2 w-60 glass-red-glow rounded-xl overflow-hidden shadow-xl z-50"
                  >
                    <div className="px-4 py-3 border-b border-white/5">
                      <div className="font-display font-bold text-sm truncate">{user.username || user.name}</div>
                      <div className="text-xs text-zinc-400 truncate">{user.email}</div>
                      {user.role === "admin" && (
                        <span className="inline-block mt-1 text-[10px] uppercase font-mono tracking-widest text-red-300 bg-red-950/50 border border-red-500/30 rounded-full px-2 py-0.5">
                          Admin
                        </span>
                      )}
                    </div>
                    <MenuItem to="/profile" label="Profile" icon={UserIcon} onClick={() => setMenuOpen(false)} />
                    <MenuItem to="/profile" label="Dashboard" icon={LayoutDashboard} onClick={() => setMenuOpen(false)} />
                    {user.role === "admin" && (
                      <MenuItem to="/admin" label="Admin Console" icon={LayoutDashboard} onClick={() => setMenuOpen(false)} tid="menu-admin" />
                    )}
                    <button
                      onClick={handleLogout}
                      data-testid="menu-logout"
                      className="w-full text-left px-4 py-3 text-sm text-red-300 hover:bg-red-950/40 transition-colors flex items-center gap-2 border-t border-white/5"
                    >
                      <LogOut size={14} /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <button
                  onClick={() => { setAuthMode("login"); setAuthOpen(true); }}
                  data-testid="nav-login"
                  className="btn-ghost-glass !py-2 !px-4 !text-xs"
                >
                  <LogIn size={12} /> Login
                </button>
                <button
                  onClick={() => { setAuthMode("register"); setAuthOpen(true); }}
                  data-testid="nav-register"
                  className="btn-liquid !py-2 !px-4 !text-xs"
                >
                  <UserPlus size={12} /> Register
                </button>
              </div>
            )}
            <button
              onClick={() => setOpen((v) => { const next = !v; if (next) setMenuOpen(false); return next; })}
              data-testid="nav-toggle"
              className="lg:hidden h-9 w-9 rounded-full glass-dark flex items-center justify-center"
            >
              {open ? <X size={16} /> : <Menu size={16} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-white/5 bg-black/80 backdrop-blur-xl">
            <div className="max-w-7xl mx-auto px-6 py-3 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `text-sm font-medium py-2.5 px-3 rounded-lg ${
                      isActive ? "bg-red-950/40 text-white" : "text-zinc-300 hover:bg-white/5"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              {!user && (
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => { setOpen(false); setAuthMode("login"); setAuthOpen(true); }}
                    data-testid="nav-mobile-login"
                    className="btn-ghost-glass flex-1 !py-2 !text-xs"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => { setOpen(false); setAuthMode("register"); setAuthOpen(true); }}
                    data-testid="nav-mobile-register"
                    className="btn-liquid flex-1 !py-2 !text-xs"
                  >
                    Register
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialMode={authMode} />
    </>
  );
}

function MenuItem({ to, label, icon: Icon, onClick, tid }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={tid}
      className="w-full text-left px-4 py-2.5 text-sm text-zinc-300 hover:bg-white/5 hover:text-white transition-colors flex items-center gap-2"
    >
      <Icon size={14} className="text-red-400" /> {label}
    </Link>
  );
}

export function UserAvatar({ user, size = 32 }) {
  if (!user) return null;
  if (user.avatar_url) {
    return (
      <img
        src={resolveAsset(user.avatar_url)}
        alt=""
        className="rounded-full object-cover border border-white/15"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center font-display font-bold text-white border border-white/15 shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        background: "linear-gradient(180deg, #ff3333 0%, #7a0000 100%)",
      }}
    >
      {initials(user.username || user.name || user.email)}
    </div>
  );
}
